import { ComponentProps, KeyboardEvent, MouseEvent, RefObject, useCallback, useMemo, useRef } from 'react';

export interface UseCategoricalChartAsListboxProps {
  /** The chart `id`. Options are located by the `id<id>__<type>-<index>` ids the chart gives them. */
  id: string;
  /** The chart's `chartRef`: the element that becomes the listbox and the single tab stop. */
  ref: RefObject<HTMLDivElement | null>;
  /** Id segment of the option elements: `arc` for `PieChart` slices, `bar` for `BarChart` bars. */
  type: 'arc' | 'bar';
  /** Number of rendered options. Zero-value slices and bars are not drawn, so leave them out. */
  optionCount: number;
  /** Which arrow keys move between options. */
  orientation?: 'horizontal' | 'vertical';
  /** Whether several options can be selected at once. */
  multiselectable?: boolean;
}

/**
 * Gives a categorical chart the roles, attributes and keyboard handling of a listbox: the chart is the
 * tab stop, arrow keys move `aria-activedescendant` and the `.focused` class between its options, and
 * Enter or Space activates the active option as a click would.
 *
 * Spread the result on `chartProps`, give the same `ref` to `chartRef`, and render the options with
 * `role="option"` and `tabIndex={-1}` (`sliceRole` + `sliceProps`, or `barRole` + `barProps`).
 *
 * Returns `undefined` while there is no option, so an empty chart is not a tab stop.
 */
export const useCategoricalChartAsListbox = ({
  id,
  ref,
  type,
  optionCount,
  orientation = 'horizontal',
  multiselectable = false,
}: UseCategoricalChartAsListboxProps): ComponentProps<'div'> | undefined => {
  const activeIndexRef = useRef(0);

  const getOption = useCallback(
    (index: number) => ref.current?.querySelector<SVGElement>(`[id="id${id}__${type}-${index}"]`) ?? null,
    [id, ref, type]
  );

  const setActive = useCallback(
    (index: number) => {
      getOption(activeIndexRef.current)?.classList.remove('focused');
      activeIndexRef.current = index;
      ref.current?.setAttribute('aria-activedescendant', `id${id}__${type}-${index}`);
      getOption(index)?.classList.add('focused');
    },
    [getOption, id, ref, type]
  );

  return useMemo(() => {
    if (optionCount <= 0) return undefined;

    const forwards = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
    const backwards = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';

    const step = (delta: number) => setActive((activeIndexRef.current + delta + optionCount) % optionCount);

    return {
      role: 'listbox',
      tabIndex: 0,
      'aria-activedescendant': '',
      'aria-multiselectable': multiselectable,
      'aria-orientation': orientation,
      onFocus: () => setActive(activeIndexRef.current),
      onBlur: () => {
        getOption(activeIndexRef.current)?.classList.remove('focused');
        activeIndexRef.current = 0;
        ref.current?.setAttribute('aria-activedescendant', '');
      },
      // A pointer click must not move the focus into the chart: the option (tabIndex -1) would take it,
      // paint the `.focused` ring on the active option instead of the clicked one, and swallow the
      // arrow keys it then receives.
      onMouseDown: (event: MouseEvent<HTMLDivElement>) => event.preventDefault(),
      onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
        switch (event.code) {
          case forwards:
            event.preventDefault();
            step(1);
            break;
          case backwards:
            event.preventDefault();
            step(-1);
            break;
          case 'Home':
            event.preventDefault();
            setActive(0);
            break;
          case 'End':
            event.preventDefault();
            setActive(optionCount - 1);
            break;
          case 'Enter':
          case 'Space':
            event.preventDefault();
            // Bubbling, so React's delegated `onClick` on the option receives it.
            getOption(activeIndexRef.current)?.dispatchEvent(
              new window.MouseEvent('click', { bubbles: true, cancelable: true })
            );
            break;
          default:
            break;
        }
      },
    };
  }, [getOption, multiselectable, optionCount, orientation, ref, setActive]);
};
